import {
  AttachMoney,
  Calculate,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import CustomButton from 'lib/designSystem/Button';
import CustomDialogTitle from 'lib/designSystem/DialogTitle';
import {
  calculateRiskPremium,
  RiskPremiumCalculationResult,
  RiskPremiumRates,
} from 'lib/functions/riskPremiumCalculator';
import { formatMoney, formatMoneyUF } from 'lib/functions/utils';
import {
  getConversionRatesFromRedux,
  getCurrencyConversionRate,
} from 'lib/hooks/useConvertionRates';
import { SubProductCreditLineConcentration } from 'lib/types/models';
import React, { useEffect, useState } from 'react';

import { WarrantyFormData } from './CreateWarrantyDialog';

type CalculationResult = RiskPremiumCalculationResult;
type SubProduct = SubProductCreditLineConcentration;

interface WarrantyCreationConfirmationProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  formData: WarrantyFormData;
  rateControls: RiskPremiumRates;
  subProductsData: SubProduct[];
  loading: boolean;
}

const WarrantyCreationConfirmation: React.FC<
  WarrantyCreationConfirmationProps
> = ({
  open,
  onClose,
  onConfirm,
  formData,
  rateControls,
  subProductsData,
  loading,
}) => {
  const [originalCalculation, setOriginalCalculation] =
    useState<CalculationResult | null>(null);
  const [modifiedCalculation, setModifiedCalculation] =
    useState<CalculationResult | null>(null);

  const { quotedAmount, quotedCurrency, startDate, endDate, type } = formData;

  const daysDifference =
    startDate && endDate
      ? Math.ceil(
          (endDate.toDate().getTime() - startDate.toDate().getTime()) /
            (1000 * 60 * 60 * 24),
        )
      : 0;
  const amount = Number(quotedAmount);

  useEffect(() => {
    const performCalculations = () => {
      if (!amount || !daysDifference || !quotedCurrency || !type) {
        setOriginalCalculation(null);
        setModifiedCalculation(null);
        return;
      }

      const original = calculateRiskPremium({
        quotedAmount: amount,
        days: daysDifference,
        quotedCurrency,
        type,
        subProductsData,
        useConcentrationRates: true,
      });

      const modified = calculateRiskPremium({
        quotedAmount: amount,
        days: daysDifference,
        quotedCurrency,
        type,
        rates: rateControls,
        subProductsData,
        useConcentrationRates: true,
      });

      setOriginalCalculation(original);
      setModifiedCalculation(modified);
    };

    performCalculations();
  }, [
    amount,
    daysDifference,
    quotedCurrency,
    type,
    rateControls,
    subProductsData,
  ]);

  const originalPremium = originalCalculation?.amount || 0;
  const modifiedPremium = modifiedCalculation?.amount || 0;
  const premiumDifference = modifiedPremium - originalPremium;
  const hasRateChanges = premiumDifference !== 0;

  const originalRates = {
    variableRate: originalCalculation?.originalVariableRate,
    fixedRate: originalCalculation?.originalFixedRate,
    variableDiscount: originalCalculation?.variableDiscount,
    fixedDiscount: originalCalculation?.fixedDiscount,
  };

  const amountFormatted =
    quotedCurrency === 'UF'
      ? formatMoneyUF(amount)
      : `${quotedCurrency} ${formatMoney(amount)}`;

  const conversionRates = getConversionRatesFromRedux();
  const conversionRate = getCurrencyConversionRate(
    quotedCurrency,
    conversionRates,
  );
  const amountInCLP = amount * conversionRate;
  const showCLPBreakdown = quotedCurrency === 'UF' || quotedCurrency === 'USD';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          margin: '20px 20px',
        },
      }}
    >
      <DialogContent sx={{ px: 3 }}>
        <CustomDialogTitle setOpen={onClose}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                backgroundColor: hasRateChanges
                  ? 'warning.main'
                  : 'success.main',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <Calculate sx={{ fontSize: '1rem' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1.1rem',
              }}
            >
              Confirmación de Creación de Garantía
            </Typography>
            <Chip
              label={hasRateChanges ? 'Tasas Modificadas' : 'Tasas Estándar'}
              size="small"
              color={hasRateChanges ? 'warning' : 'success'}
              variant="outlined"
              sx={{ height: '20px', fontSize: '0.7rem' }}
            />
          </Stack>
        </CustomDialogTitle>
        <Grid container spacing={3}>
          {/* Main Info */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AttachMoney
                    sx={{ fontSize: '0.8rem', color: 'success.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Monto de Garantía
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.85rem' }}
                  >
                    {amountFormatted}
                  </Typography>
                  {showCLPBreakdown && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.6rem', display: 'block', mt: 0.5 }}
                    >
                      ≈ {formatMoney(amountInCLP)} CLP
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Calculate
                    sx={{ fontSize: '0.8rem', color: 'primary.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Costo de emisión
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.85rem' }}
                  >
                    {formatMoney(modifiedPremium)} CLP
                  </Typography>
                </Box>
              </Box>

              {hasRateChanges && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor:
                        premiumDifference > 0
                          ? 'rgba(76, 175, 80, 0.1)'
                          : 'rgba(244, 67, 54, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {premiumDifference > 0 ? (
                      <TrendingUpIcon
                        sx={{ fontSize: '0.8rem', color: 'success.main' }}
                      />
                    ) : (
                      <TrendingDownIcon
                        sx={{ fontSize: '0.8rem', color: 'error.main' }}
                      />
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      Variación de costo de emisión
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{
                        fontSize: '0.85rem',
                        color:
                          premiumDifference > 0 ? 'success.main' : 'error.main',
                      }}
                    >
                      {premiumDifference > 0 ? '+' : ''}
                      {formatMoney(premiumDifference)} CLP
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Rate Changes */}
          {hasRateChanges && (
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: '0.8rem', mb: 1 }}
                >
                  Cambios en Tasas:
                </Typography>

                {rateControls.variableRate !== originalRates.variableRate && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      Tasa Variable:
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {originalRates.variableRate?.toFixed(2)}%
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                        →
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          fontSize: '0.7rem',
                          color:
                            (rateControls.variableRate ?? 0) >
                            (originalRates.variableRate ?? 0)
                              ? 'success.main'
                              : 'error.main',
                        }}
                      >
                        {rateControls.variableRate?.toFixed(2)}%
                      </Typography>
                    </Box>
                  </Box>
                )}

                {rateControls.fixedRate !== originalRates.fixedRate && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      Tasa Fija:
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {originalRates.fixedRate?.toFixed(2)} UF
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                        →
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          fontSize: '0.7rem',
                          color:
                            (rateControls.fixedRate ?? 0) >
                            (originalRates.fixedRate ?? 0)
                              ? 'success.main'
                              : 'error.main',
                        }}
                      >
                        {rateControls.fixedRate?.toFixed(2)} UF
                      </Typography>
                    </Box>
                  </Box>
                )}

                {rateControls.variableDiscount !==
                  originalRates.variableDiscount && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      Descuento Variable:
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {originalRates.variableDiscount?.toFixed(2)}%
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                        →
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          fontSize: '0.7rem',
                          color:
                            (rateControls.variableDiscount ?? 0) >
                            (originalRates.variableDiscount ?? 0)
                              ? 'error.main'
                              : 'success.main',
                        }}
                      >
                        {rateControls.variableDiscount?.toFixed(2)}%
                      </Typography>
                    </Box>
                  </Box>
                )}

                {rateControls.fixedDiscount !== originalRates.fixedDiscount && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      Descuento Fijo:
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {originalRates.fixedDiscount?.toFixed(2)}%
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                        →
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          fontSize: '0.7rem',
                          color:
                            (rateControls.fixedDiscount ?? 0) >
                            (originalRates.fixedDiscount ?? 0)
                              ? 'error.main'
                              : 'success.main',
                        }}
                      >
                        {rateControls.fixedDiscount?.toFixed(2)}%
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>

        {hasRateChanges && (
          <Alert
            severity="warning"
            icon={<WarningIcon sx={{ fontSize: '0.9rem' }} />}
            sx={{
              mt: 2,
              borderRadius: 0.5,
              backgroundColor: 'rgba(255, 152, 0, 0.08)',
              border: '1px solid rgba(255, 152, 0, 0.2)',
              py: 0.75,
              '& .MuiAlert-message': { py: 0.25 },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
              Tasas Modificadas
            </AlertTitle>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
            >
              Las tasas han sido modificadas respecto a la cotización original.
              El costo de emisión ha variado en{' '}
              {formatMoney(Math.abs(premiumDifference))} CLP (
              {premiumDifference > 0 ? 'incremento' : 'reducción'}).
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'flex-end', gap: 2 }}>
        <CustomButton color="secondary" onClick={onClose}>
          Cancelar
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={onConfirm}
          disabled={false}
          loading={loading}
        >
          Confirmar y Crear Garantía
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default WarrantyCreationConfirmation;
